import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import {memo, useCallback} from 'react'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import {useSyncedBooleanQueryParam} from '../../../../hooks/routes'
import {getChangesArg} from '../../../../rest/arguments'
import {restApi} from '../../../../services/rest'
import type {BuildTypeId} from '../../../../types'
import {UserProperties} from '../../../../utils/userProperties'
import type {CommiterSelectQuery} from '../../../common/UserSelect/__generated__/CommiterSelectQuery.graphql'
import CommiterSelect from '../../../common/UserSelect/CommiterSelect'
import type {UserSelectSelectedItem} from '../../../common/UserSelect/UserSelect.types'
import {useChangesFilter} from '../Changes.hooks'
import ChangesContentFilter from '../ChangesContentFilter/ChangesContentFilter'

import BuildsFilter from './BuildsFilter/BuildsFilter'

import styles from './ChangesFilter.css'

type Props = {
  className?: string
  buildTypeId?: BuildTypeId
  baseLocator: string
  withBuildsFilter?: boolean
  withBuildsToggle?: boolean
  withChangesFromDependenciesToggle?: boolean
  withIncludeChangesInSettingsToggle?: boolean
  commiterSelectQueryRef: PreloadedQuery<CommiterSelectQuery>
}

function ChangesFilter({
  className,
  buildTypeId,
  baseLocator,
  withBuildsFilter,
  withBuildsToggle,
  withChangesFromDependenciesToggle,
  withIncludeChangesInSettingsToggle,
  commiterSelectQueryRef,
}: Props) {
  const dependencyChangeLocator = `${baseLocator},count:1,changesFromDependencies:true`
  const settingsChangeLocator = `${baseLocator},count:1,versionedSettings:true`

  const {filterOptions, onChangeFilter} = useChangesFilter(buildTypeId)
  const {versionedSettings, showDependenciesChanges} = filterOptions

  const {hasDependencyChanges} = restApi.endpoints.getAllChanges.useQuery(
    getChangesArg(dependencyChangeLocator),
    {
      skip: !withChangesFromDependenciesToggle,
      selectFromResult: ({data}) => ({
        hasDependencyChanges: (data?.changes?.result?.length ?? 0) > 0,
      }),
    },
  )
  const {hasSettingsChanges} = restApi.endpoints.getAllChanges.useQuery(
    getChangesArg(settingsChangeLocator),
    {
      skip: !withIncludeChangesInSettingsToggle,
      selectFromResult: ({data}) => ({
        hasSettingsChanges: (data?.changes?.result?.length ?? 0) > 0,
      }),
    },
  )

  const [showFiles, setShowFiles] = useSyncedBooleanQueryParam({
    param: 'showFiles',
    userProperty: UserProperties.SHOW_ALL_CHANGES_FILES,
  })
  const [showGraph, setShowGraph] = useSyncedBooleanQueryParam({
    param: 'showGraph',
    userProperty: UserProperties.SHOW_CHANGES_GRAPH,
    defaultTrue: true,
  })
  const [showBuilds, setShowBuilds] = useSyncedBooleanQueryParam({
    param: 'showBuilds',
    userProperty: UserProperties.SHOW_CHANGES_BUILDS,
    defaultTrue: true,
  })

  const onChangeUsername = useCallback(
    (data: UserSelectSelectedItem) => onChangeFilter({username: data?.value ?? null}),
    [onChangeFilter],
  )
  const onChangeVersionedSettings = useCallback(
    () => onChangeFilter({versionedSettings: !versionedSettings}),
    [onChangeFilter, versionedSettings],
  )
  const onChangeShowDependenciesChanges = useCallback(
    () => onChangeFilter({showDependenciesChanges: !showDependenciesChanges}),
    [onChangeFilter, showDependenciesChanges],
  )
  const onChangeShowFiles = useCallback(() => setShowFiles(!showFiles), [setShowFiles, showFiles])
  const onChangeShowGraph = useCallback(() => setShowGraph(!showGraph), [setShowGraph, showGraph])
  const onChangeShowBuilds = useCallback(
    () => setShowBuilds(!showBuilds),
    [setShowBuilds, showBuilds],
  )

  return (
    <div className={className} data-test-changes-filter>
      <div className={styles.line}>
        <CommiterSelect
          className={styles.filterControl}
          queryRef={commiterSelectQueryRef}
          selected={filterOptions.username}
          onChange={onChangeUsername}
        />
        {withBuildsFilter && (
          <BuildsFilter
            className={styles.filterControl}
            fromBuildNumber={filterOptions.fromBuildNumber}
            toBuildNumber={filterOptions.toBuildNumber}
            onChangeFilter={onChangeFilter}
          />
        )}
        {hasSettingsChanges && (
          <div className={styles.filterControl}>
            <Checkbox onChange={onChangeVersionedSettings} checked={versionedSettings}>
              {'Include changes in settings'}
            </Checkbox>
          </div>
        )}
        {withChangesFromDependenciesToggle &&
          hasDependencyChanges &&
          showDependenciesChanges != null && (
            <div className={styles.filterControl}>
              <Checkbox
                onChange={onChangeShowDependenciesChanges}
                checked={showDependenciesChanges}
              >
                {'Show changes from dependencies'}
              </Checkbox>
            </div>
          )}
      </div>
      <div className={styles.line}>
        <ChangesContentFilter
          className={styles.filterControl}
          revision={filterOptions.revision}
          comment={filterOptions.comment}
          path={filterOptions.path}
          onChangeFilter={onChangeFilter}
        />
      </div>
      <div className={styles.line}>
        <div className={styles.filterControl}>
          <Checkbox onChange={onChangeShowFiles} checked={showFiles}>
            {'Show files'}
          </Checkbox>
        </div>
        <div className={styles.filterControl}>
          <Checkbox onChange={onChangeShowGraph} checked={showGraph}>
            {'Show graph'}
          </Checkbox>
        </div>
        {withBuildsToggle && (
          <div className={styles.filterControl}>
            <Checkbox onChange={onChangeShowBuilds} checked={showBuilds}>
              {'Show builds'}
            </Checkbox>
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(ChangesFilter)
